/** Etched legends, the contact shadow, and the studio backdrop. */

import type * as THREE from 'three';
import { canvas, ctx2d, memo, ownedTex, tex } from './canvas.ts';

export interface DecalOptions {
  w?: number;
  h?: number;
  size?: number;
  weight?: number;
  color?: string;
  letterSpacing?: string;
  align?: CanvasTextAlign;
}

const FONT_STACK = '-apple-system, "SF Pro Display", "Segoe UI", Inter, system-ui, sans-serif';

/** Small etched-text decal for deck legends (START/STOP, PITCH, 33, 45). */
export function decalTexture(text: string, options: DecalOptions = {}): THREE.CanvasTexture {
  const {
    w = 512,
    h = 128,
    size = 62,
    weight = 600,
    color = 'rgba(74,86,80,0.92)',
    letterSpacing = '0.16em',
    align = 'center',
  } = options;

  const c = canvas(w, h);
  const g = ctx2d(c);
  g.clearRect(0, 0, w, h);
  g.font = `${weight} ${size}px ${FONT_STACK}`;
  g.letterSpacing = letterSpacing;
  g.textAlign = align;
  g.textBaseline = 'middle';
  g.fillStyle = color;
  const x = align === 'center' ? w / 2 : align === 'right' ? w - 8 : 8;
  g.fillText(text, x, h / 2 + 2);
  return ownedTex(c, 8);
}

/** Soft round contact shadow, so the deck never looks like it floats. */
export function radialShadow(size = 256): THREE.CanvasTexture {
  return memo(`rshadow-${size}`, () => {
    const c = canvas(size);
    const g = ctx2d(c);
    const gr = g.createRadialGradient(size / 2, size / 2, 0, size / 2, size / 2, size / 2);
    gr.addColorStop(0, 'rgba(0,0,0,0.42)');
    gr.addColorStop(0.55, 'rgba(0,0,0,0.15)');
    gr.addColorStop(1, 'rgba(0,0,0,0)');
    g.fillStyle = gr;
    g.fillRect(0, 0, size, size);
    return tex(c, { srgb: true });
  });
}

/** Studio backdrop gradient used for `scene.background`. */
export function backdropTexture(): THREE.CanvasTexture {
  return memo('backdrop', () => {
    const c = canvas(16, 512);
    const g = ctx2d(c);
    const gr = g.createLinearGradient(0, 0, 0, 512);
    gr.addColorStop(0, '#FCFEFD');
    gr.addColorStop(0.55, '#F1F6F3');
    gr.addColorStop(1, '#E2EBE6');
    g.fillStyle = gr;
    g.fillRect(0, 0, 16, 512);
    return ownedTex(c, 1);
  });
}
